/**
 * React Hook for YJS Collaboration
 *
 * This hook provides a React interface for the YJS collaboration service,
 * managing collaborative editing state and providing real-time updates
 * about active editors.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AwarenessState,
  ChangesRevertedData,
  EditorInfo,
  FieldValueChangeData,
  JobReorderConfirmationData,
  JobReorderData,
  yjsCollaborationService
} from 'services/yjsCollaborationService';

export interface UseYJSCollaborationReturn {
  // Connection state
  isConnected: boolean;

  // Editor information
  allEditors: EditorInfo[];
  roomEditors: (roomName: string) => EditorInfo[];
  totalEditors: number;

  // Actions
  startEditingRoom: (roomName: string, jobId?: string) => void;
  stopEditingRoom: (roomName: string) => void;

  // Field-level editing actions
  startEditingField: (
    roomName: string,
    jobId: string,
    fieldName: string,
    fieldType: string,
    fieldValue?: any
  ) => void;
  stopEditingField: (roomName: string, jobId: string, fieldName: string) => void;
  updateFieldValue: (roomName: string, jobId: string, fieldName: string, fieldValue: any) => void;

  // Room-specific editor counts
  getRoomEditorCount: (roomName: string) => number;
  getRoomEditorNames: (roomName: string) => string[];

  // Field-specific information
  getFieldEditors: (roomName: string, fieldName: string, jobId: string) => EditorInfo[];
  isFieldBeingEdited: (roomName: string, fieldName: string, jobId: string) => boolean;

  // Field value change handling
  onFieldValueChange: (handler: (data: FieldValueChangeData) => void) => () => void;

  // Job reorder handling
  sendJobReorder: (roomName: string, jobSerials: Record<string, number>) => void;
  onJobReorderUpdate: (handler: (data: JobReorderData) => void) => () => void;
  onJobReorderConfirmation: (handler: (data: JobReorderConfirmationData) => void) => () => void;

  // Changes reverted handling
  onChangesReverted: (handler: (data: ChangesRevertedData) => void) => () => void;
}

/**
 * Hook for managing YJS collaborative editing
 *
 * @returns Object containing collaboration state and methods
 */
export const useYJSCollaboration = (): UseYJSCollaborationReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [awarenessState, setAwarenessState] = useState<AwarenessState>({
    editors: new Map(),
    totalEditors: 0
  });

  // Initialize YJS service
  useEffect(() => {
    yjsCollaborationService.initialize();

    // Set up awareness change handler
    const unsubscribe = yjsCollaborationService.onAwarenessChange(state => {
      setAwarenessState(state);
    });

    // Check initial connection status
    setIsConnected(yjsCollaborationService.isWebSocketConnected());

    // Set up connection status polling
    const connectionCheckInterval = setInterval(() => {
      setIsConnected(yjsCollaborationService.isWebSocketConnected());
    }, 1000);

    return () => {
      unsubscribe();
      clearInterval(connectionCheckInterval);
    };
  }, []);

  // Get all editors (including those not currently editing)
  const allEditors = Array.from(awarenessState.editors.values());

  // Get editors for a specific room
  const roomEditors = useCallback(
    (roomName: string): EditorInfo[] => {
      return allEditors.filter(editor => editor.roomName === roomName && editor.isEditing);
    },
    [allEditors]
  );

  // Get total number of editors
  const totalEditors = awarenessState.totalEditors;

  // Start editing a room
  const startEditingRoom = useCallback((roomName: string, jobId?: string) => {
    yjsCollaborationService.startEditingRoom(roomName, jobId);
  }, []);

  // Stop editing a room
  const stopEditingRoom = useCallback((roomName: string) => {
    yjsCollaborationService.stopEditingRoom(roomName);
  }, []);

  // Get editor count for a room
  const getRoomEditorCount = useCallback(
    (roomName: string): number => {
      return roomEditors(roomName).length;
    },
    [roomEditors]
  );

  // Get editor names for a room
  const getRoomEditorNames = useCallback(
    (roomName: string): string[] => {
      return roomEditors(roomName).map(editor => editor.userName);
    },
    [roomEditors]
  );

  // Field-level editing methods
  const startEditingField = useCallback(
    (roomName: string, jobId: string, fieldName: string, fieldType: string, fieldValue?: any) => {
      yjsCollaborationService.startEditingField(roomName, jobId, fieldName, fieldType, fieldValue);
    },
    []
  );

  const stopEditingField = useCallback((roomName: string, jobId: string, fieldName: string) => {
    yjsCollaborationService.stopEditingField(roomName, jobId, fieldName);
  }, []);

  const updateFieldValue = useCallback(
    (roomName: string, jobId: string, fieldName: string, fieldValue: any) => {
      yjsCollaborationService.updateFieldValue(roomName, jobId, fieldName, fieldValue);
    },
    []
  );

  // Get editors for a specific field
  const getFieldEditors = useCallback(
    (roomName: string, fieldName: string, jobId: string): EditorInfo[] => {
      const filtered = allEditors.filter(
        editor =>
          editor.roomName === roomName &&
          editor.currentField?.fieldName === fieldName &&
          editor.currentField?.jobId === jobId &&
          editor.currentField?.isEditing
      );

      return filtered;
    },
    [allEditors]
  );

  // Check if a field is being edited
  const isFieldBeingEdited = useCallback(
    (roomName: string, fieldName: string, jobId: string): boolean => {
      return getFieldEditors(roomName, fieldName, jobId).length > 0;
    },
    [getFieldEditors]
  );

  // Field value change handler
  const onFieldValueChange = useCallback((handler: (data: FieldValueChangeData) => void) => {
    return yjsCollaborationService.onFieldValueChange(handler);
  }, []);

  // Job reorder methods
  const sendJobReorder = useCallback((roomName: string, jobSerials: Record<string, number>) => {
    yjsCollaborationService.sendJobReorder(roomName, jobSerials);
  }, []);

  const onJobReorderUpdate = useCallback((handler: (data: JobReorderData) => void) => {
    return yjsCollaborationService.onJobReorderUpdate(handler);
  }, []);

  const onJobReorderConfirmation = useCallback(
    (handler: (data: JobReorderConfirmationData) => void) => {
      return yjsCollaborationService.onJobReorderConfirmation(handler);
    },
    []
  );

  // Changes reverted handler
  const onChangesReverted = useCallback((handler: (data: ChangesRevertedData) => void) => {
    return yjsCollaborationService.onChangesReverted(handler);
  }, []);

  return {
    isConnected,
    allEditors,
    roomEditors,
    totalEditors,
    startEditingRoom,
    stopEditingRoom,
    startEditingField,
    stopEditingField,
    updateFieldValue,
    getRoomEditorCount,
    getRoomEditorNames,
    getFieldEditors,
    isFieldBeingEdited,
    onFieldValueChange,
    sendJobReorder,
    onJobReorderUpdate,
    onJobReorderConfirmation,
    onChangesReverted
  };
};
